import { useState, useEffect, useRef } from 'react';
import { cloneDeep, debounce, isEqual } from 'lodash';
import { Questionnaire } from '@/typings/Questionnaire';
import { useQuestionnaireFormContext } from '@/components/Questionnaire/QuestionnaireProvider';
import HandleAiAgentAutofills from './HandleAiAgentAutofills';
import HandleSectionsCondition from './HandleSectionsCondition';
import HandleAllAutofills from './HandleAutofills';
import HandleAutoSave from './HandleAutoSave';
import HandleFieldDynamicOptions from './HandleFieldDynamicOptions';
import HandleQuestionnaireActions from './HandleQuestionnaireActions';

type Props = {
  formData: Questionnaire.Questionnaire;
  onStartAgent: Parameters<typeof HandleAiAgentAutofills>[0]['onStartAgent'];
  isSectionsConditionEnabled: boolean;
  isAutoSaveEnabled: boolean;
  isAutofillEnabled: boolean;
  isAiAgentsEnabled: boolean;
  isDynamicOptionsEnabled: boolean;
  isActionsEnabled: boolean;
  filterAiAgents?: Parameters<typeof HandleAiAgentAutofills>[0]['filter'];
  filterAutofills?: Parameters<typeof HandleAiAgentAutofills>[0]['filter'];
  currentTableRowKey?: string | null;
};

const HandleWatchers: React.FC<Props> = ({
  formData,
  onStartAgent,
  isSectionsConditionEnabled,
  isAutoSaveEnabled,
  isAutofillEnabled,
  isAiAgentsEnabled,
  isDynamicOptionsEnabled,
  isActionsEnabled,
  filterAiAgents,
  filterAutofills,
  currentTableRowKey,
}) => {
  const {
    formMethods,
  } = useQuestionnaireFormContext();

  const [formValues, setFormValues] = useState<Record<string, unknown> | null>(null);
  const previousFormValuesRef = useRef<Record<string, unknown> | null>(null);

  useEffect(() => {
    // Sometimes list subFields are changed in previousFormValues before the watch is triggered.
    // Seems like react hook form is mutating the object... If not that, I'm not sure what else it could be.
    const initialValues = cloneDeep(formMethods.getValues());
    previousFormValuesRef.current = initialValues;
    setFormValues(initialValues);

    const debounceWatch = debounce((value) => {
      // Entering and blurring, even withouot changing, a field triggers the watch
      // The strict equal is false, however, so need to deep compare to see if there really is a change.
      const valueIsChanged = !isEqual(previousFormValuesRef.current, value);
      if (valueIsChanged) {
        previousFormValuesRef.current = cloneDeep(value);
        setFormValues(previousFormValuesRef.current);
      }
    }, 500);

    const subscription = formMethods.watch((value) => {
      debounceWatch(value);
    });
    return () => subscription.unsubscribe();
  }, [formMethods]);

  // Some handlers formValues to be set on mount.
  // But to reduce re-renders while setting formValues and previousFormValues we need to do those in one useEffect.
  if (!formValues) {
    return null;
  }

  return (
    <>
      {isSectionsConditionEnabled && (
        <HandleSectionsCondition
          sections={formData.sections}
        />
      )}
      {isAutofillEnabled && (
        <HandleAllAutofills
          formData={formData}
          formValues={formValues}
          filter={filterAutofills}
          currentTableRowKey={currentTableRowKey}
        />
      )}
      {isAiAgentsEnabled && <HandleAiAgentAutofills
        filter={filterAiAgents}
        questionnaireId={formData.id}
        onStartAgent={onStartAgent}
      />}
      {isAutoSaveEnabled && (
        <HandleAutoSave
          formData={formData}
          formValues={formValues}
        />
      )}
      {isDynamicOptionsEnabled && <HandleFieldDynamicOptions
        formData={formData}
        formValues={formValues}
      />}
      {isActionsEnabled && <HandleQuestionnaireActions
        formData={formData}
      />}
    </>
  );
};

export default HandleWatchers;
